/**
 * ADR-0223 §3.4 — Meetings (`/app/collab/meetings`), three tabs as route
 * segments: Recurring (a card per series), One-off (by month, with the
 * 3-field quick create), Calendar (a month grid, Sunday first, chips by
 * series colour and state). The counts and every line are served.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { CollabPage, errMessage, type CalendarMonth, type Occurrence, type OneOffMonth, type SeriesCard } from "../components/collab.js";

type Tab = "recurring" | "one-off" | "calendar";

export function CollabMeetings(props: { tab: Tab; entitlement?: string }) {
  const builder = props.entitlement === "builder" || props.entitlement === "admin";
  const [series, setSeries] = useState<SeriesCard[] | null>(null);
  const [months, setMonths] = useState<OneOffMonth[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([apiFetch<{ series: SeriesCard[] }>("/api/tenant/collab/series"), apiFetch<{ months: OneOffMonth[] }>("/api/tenant/collab/one-offs")])
      .then(([s, o]) => {
        if (cancelled) return;
        setSeries(s.series);
        setMonths(o.months);
      })
      .catch((e) => {
        if (!cancelled) setError(errMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const oneOffCount = months?.reduce((n, m) => n + m.sessions.length, 0) ?? 0;
  const tabs: Array<[Tab, string, string, number | null]> = [
    ["recurring", "/app/collab/meetings", "Recurring", series?.length ?? null],
    ["one-off", "/app/collab/meetings/one-off", "One-off", months ? oneOffCount : null],
    ["calendar", "/app/collab/meetings/calendar", "Calendar", null],
  ];

  return (
    <CollabPage title="Meetings">
      <div className="collabHead">
        <div className="collabKicker">Atlas Collab</div>
        <nav className="collabTabRow" aria-label="Meetings" style={{ marginTop: 6 }}>
          {tabs.map(([key, to, label, count]) => (
            <Link key={key} to={to} className={`collabTab${props.tab === key ? " collabTabOn" : ""}`}>
              {label}
              {count !== null ? <span className="cnt">{count}</span> : null}
            </Link>
          ))}
        </nav>
      </div>
      {error ? <ErrorState detail={error} /> : null}
      {!error && props.tab === "recurring" ? series === null ? <LoadingState /> : <RecurringView series={series} builder={builder} /> : null}
      {!error && props.tab === "one-off" ? months === null ? <LoadingState /> : <OneOffView months={months} onCreated={(m) => setMonths(m)} /> : null}
      {!error && props.tab === "calendar" ? <CalendarView /> : null}
    </CollabPage>
  );
}

/** Pure, for the tests: one card per series. */
export function RecurringView(props: { series: SeriesCard[]; builder: boolean }) {
  return (
    <section>
      <div className="collabSecHead">
        <span className="count">By subject</span>
        {props.builder ? (
          <Link className="more" to="/app/collab/meetings/series/new">
            + New recurring meeting
          </Link>
        ) : null}
      </div>
      {props.series.length === 0 ? <div className="collabEmpty">No recurring meetings yet.{props.builder ? " Create the first one above." : ""}</div> : null}
      <div className="collabSeriesRow">
        {props.series.map((s) => {
          const inner = (
            <>
              <div>
                <div className="collabSeriesName">{s.name}</div>
                <div className="collabSeriesWhen">{s.when_line}</div>
              </div>
              {s.next ? (
                <div className="collabNextBlock">
                  <span className="collabNextDate">{s.next.day_number}</span>
                  <div>
                    <div className="collabNextLabel">{s.next.label}</div>
                    <div className="collabNextWhat">{s.next.line}</div>
                  </div>
                </div>
              ) : (
                <div className="collabNextBlock">
                  <div className="collabNextWhat">No upcoming meeting.</div>
                </div>
              )}
              <div className="collabSeriesFoot">
                {s.last ? (
                  <>
                    <span>
                      Last <b>{s.last.held_on.length ? s.last.line.replace(/^Last /, "").split(" · ")[0] : ""}</b>
                    </span>
                    <span>
                      <b>{s.last.decided}</b> decided
                    </span>
                    <span>
                      <b>{s.last.raised}</b> raised
                    </span>
                  </>
                ) : (
                  <span>No session recorded yet</span>
                )}
              </div>
            </>
          );
          return (
            <div key={s.id} style={{ display: "grid", gap: 6 }}>
              <Link className="collabSeries" to={s.href}>
                {inner}
              </Link>
              {s.can.edit ? (
                <Link className="collabLinkBtn" to={`/app/collab/series/${s.id}/edit`} style={{ justifySelf: "end", fontSize: 12 }}>
                  Edit meeting
                </Link>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function OneOffView(props: { months: OneOffMonth[]; onCreated: (months: OneOffMonth[]) => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [draft, setDraft] = useState({ title: "", held_on: "", starts_at: "" });
  const [busy, setBusy] = useState(false);
  async function create() {
    if (!draft.title.trim() || !draft.held_on || busy) return;
    setBusy(true);
    try {
      const r = await apiFetch<{ session: { id: string } }>("/api/tenant/collab/sessions", { method: "POST", body: { title: draft.title.trim(), held_on: draft.held_on, starts_at: draft.starts_at || null, scheduled: true } });
      toast.push({ kind: "success", message: "Meeting added." });
      navigate(`/app/collab/sessions/${r.session.id}`);
    } catch (e) {
      toast.push({ kind: "error", message: errMessage(e) });
    } finally {
      setBusy(false);
    }
  }
  void props.onCreated;
  return (
    <section>
      <div className="collabSecHead">
        <span className="count">By date, newest first</span>
      </div>
      <div className="collabOneoff">
        <div className="collabQuickCreate" style={{ borderTop: 0 }}>
          <label className="collabLabel">
            New one-off meeting
            <input className="collabInput" value={draft.title} maxLength={200} placeholder="Subject" onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          </label>
          <label className="collabLabel">
            Date
            <input className="collabInput" type="date" value={draft.held_on} onChange={(e) => setDraft({ ...draft, held_on: e.target.value })} />
          </label>
          <label className="collabLabel">
            Time
            <input className="collabInput" type="time" value={draft.starts_at} onChange={(e) => setDraft({ ...draft, starts_at: e.target.value })} />
          </label>
          <button type="button" className="collabBtn" disabled={busy || !draft.title.trim() || !draft.held_on} onClick={() => void create()}>
            Add
          </button>
        </div>
        {props.months.length === 0 ? <div className="collabEmpty" style={{ padding: "12px 20px 16px" }}>No one-off meetings yet.</div> : null}
        {props.months.map((m) => (
          <div key={m.month}>
            <div className="collabOneoffMonth">{m.label}</div>
            {m.sessions.map((s) => (
              <Link className="collabOneoffRow" to={s.href} key={s.session_id}>
                <span className="collabOneoffDate">{s.date_label}</span>
                <span>
                  <span className="collabOneoffName">{s.title}</span>
                  <br />
                  <span className="collabOneoffSub">{s.summary ?? ""}</span>
                </span>
                <span className="collabOneoffSub">
                  {s.time ? `${s.time} · ` : ""}
                  {s.line}
                </span>
              </Link>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

const DOWS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** Pure, for the tests: the cells of a Sunday-first grid for a served month, with the leading and trailing days of the neighbours. */
export function calendarCells(month: CalendarMonth): Array<{ date: string | null; day: number; out: boolean; meetings: Occurrence[] }> {
  const [y, m] = month.month.split("-").map(Number) as [number, number];
  const first = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
  const prevDays = new Date(Date.UTC(y, m - 1, 0)).getUTCDate();
  const cells: Array<{ date: string | null; day: number; out: boolean; meetings: Occurrence[] }> = [];
  for (let i = 0; i < first; i++) cells.push({ date: null, day: prevDays - first + 1 + i, out: true, meetings: [] });
  for (const d of month.days) cells.push({ date: d.date, day: Number(d.date.slice(8, 10)), out: false, meetings: d.meetings });
  let n = 1;
  while (cells.length % 7) cells.push({ date: null, day: n++, out: true, meetings: [] });
  return cells;
}

function addMonth(month: string, n: number): string {
  const [y, m] = month.split("-").map(Number) as [number, number];
  const idx = y * 12 + (m - 1) + n;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, "0")}`;
}

function thisMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

export function CalendarView() {
  const [params, setParams] = useSearchParams();
  const month = params.get("month") && /^\d{4}-\d{2}$/.test(params.get("month")!) ? params.get("month")! : thisMonth();
  const [cal, setCal] = useState<CalendarMonth | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setCal(null);
    void apiFetch<{ calendar: CalendarMonth }>(`/api/tenant/collab/calendar?month=${encodeURIComponent(month)}`)
      .then((r) => {
        if (!cancelled) setCal(r.calendar);
      })
      .catch((e) => {
        if (!cancelled) setError(errMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [month]);
  if (error) return <ErrorState detail={error} />;
  const today = new Date().toISOString().slice(0, 10);
  const [y, m] = month.split("-").map(Number) as [number, number];
  return (
    <div className="collabCal">
      <div className="collabCalTop">
        <span className="collabCalMonth">
          {MONTH_NAMES[m - 1]} {y}
        </span>
        <span className="collabCalNav">
          <button type="button" className="collabBtn" aria-label="Previous month" onClick={() => setParams({ month: addMonth(month, -1) })}>
            ‹
          </button>
          <button type="button" className="collabBtn" aria-label="Next month" onClick={() => setParams({ month: addMonth(month, 1) })}>
            ›
          </button>
          <button type="button" className="collabBtn" style={{ fontSize: 13 }} onClick={() => setParams({})}>
            Today
          </button>
        </span>
        {cal ? (
          <span className="collabLegend">
            {cal.legend.map((l) => (
              <span key={l.name}>
                <i style={{ background: l.colour }} />
                {l.name}
              </span>
            ))}
            <span className="sep" />
            <span>
              <i className="key" style={{ background: "#E1EDF6", borderLeft: "3px solid #1D5C8A" }} />
              Recorded
            </span>
            <span>
              <i className="key" style={{ background: "#fff", boxShadow: "inset 0 0 0 1px #1D5C8A" }} />
              Next
            </span>
            <span>
              <i className="key" style={{ borderLeft: "3px dashed #1D5C8A" }} />
              Scheduled
            </span>
          </span>
        ) : null}
      </div>
      <div className="collabCalScroll">
        {cal ? (
          <div className="collabCalGrid">
            {DOWS.map((d) => (
              <div className="collabCalDow" key={d}>
                {d}
              </div>
            ))}
            {calendarCells(cal).map((c, i) => {
              const wk = i % 7 === 0 || i % 7 === 6;
              return (
                <div className={`collabCell${c.out ? " collabCellOut" : ""}${wk && !c.out ? " collabCellWknd" : ""}${c.date === today ? " collabCellToday" : ""}`} key={`${c.date ?? "out"}-${i}`}>
                  <div className="collabCellTop">
                    <span className="collabCellNum">{c.day}</span>
                  </div>
                  {c.meetings.map((o) => (
                    <Chip o={o} key={`${o.kind}:${o.series_id ?? o.session_id}:${o.date}`} />
                  ))}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ padding: 20 }}>
            <LoadingState />
          </div>
        )}
      </div>
    </div>
  );
}

function tint(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, 0.12)`;
}

/** A calendar chip, drawn by state: recorded (filled), next (outlined), scheduled (dashed), no record (grey). */
export function Chip(props: { o: Occurrence }) {
  const { o } = props;
  const cls = `collabEv${o.state === "next" ? " collabEvNext" : o.state === "scheduled" ? " collabEvSched" : o.state === "no_record" ? " collabEvNone" : ""}`;
  const sub = o.state === "recorded" ? `${o.time ? `${o.time} · ` : ""}recorded` : o.state === "next" ? `${o.time ?? ""} · next` : o.state === "no_record" ? `${o.time ?? ""} · no record` : o.time ?? "";
  const style = { "--c": o.colour, "--t": tint(o.colour) } as React.CSSProperties;
  const inner = (
    <>
      <b>{o.name}</b>
      <small>{sub}</small>
    </>
  );
  return o.href ? (
    <Link className={cls} to={o.href} style={style}>
      {inner}
    </Link>
  ) : (
    <span className={cls} style={style}>
      {inner}
    </span>
  );
}
